import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useEventListener from '../../hooks/useEventListener';

type Props = {
  hub?: string;
};

const isModDown = (e: KeyboardEvent | MouseEvent): boolean => {
  return e.altKey;
};

const findHost = (el: Element | null): Element | null => {
  return el?.closest?.('[data-delivery-id]') as Element | null;
};

type BadgeState = {
  visible: boolean;
  text: string;
  x: number;
  y: number;
};

export default function DevClickOpener({ hub = 'traegergrillsprod' }: Props) {
  const modPressedRef = useRef(false);
  const lastXRef = useRef(typeof window !== 'undefined' ? Math.floor(window.innerWidth / 2) : 0);
  const lastYRef = useRef(typeof window !== 'undefined' ? Math.floor(window.innerHeight / 2) : 0);
  const havePointerPosRef = useRef(false);
  const badgeRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [badgeState, setBadgeState] = useState<BadgeState>({
    visible: false,
    text: '',
    x: 0,
    y: 0,
  });

  const base = `https://app.amplience.net/content/#!/${hub}/authoring/content-item/edit/`;

  useEffect(() => {
    setMounted(true);
  }, []);

  const hideBadge = useCallback(() => {
    setBadgeState((prev) => ({ ...prev, visible: false }));
  }, []);

  const showBadgeAt = useCallback((host: Element, x: number, y: number) => {
    const schema = host.getAttribute('data-schema') || '';
    const id = host.getAttribute('data-delivery-id') || '';
    const text = schema || `(no schema) ${id ? `• ${id}` : ''}`;

    setBadgeState({
      visible: true,
      text,
      x,
      y,
    });
  }, []);

  const onKeyChange = useCallback((e: KeyboardEvent) => {
    const now = isModDown(e);
    const prev = modPressedRef.current;

    if (prev === now) return;

    modPressedRef.current = now;

    if (!now) {
      hideBadge();
      return;
    }

    const posX = havePointerPosRef.current ? lastXRef.current : Math.floor(window.innerWidth / 2);
    const posY = havePointerPosRef.current ? lastYRef.current : Math.floor(window.innerHeight / 2);
    const el = document.elementFromPoint(posX, posY) as Element | null;
    const host = findHost(el);
    if (host) showBadgeAt(host, posX, posY);
    else hideBadge();
  }, [hideBadge, showBadgeAt]);

  const onPointerMove = useCallback((e: MouseEvent) => {
    lastXRef.current = e.clientX;
    lastYRef.current = e.clientY;
    havePointerPosRef.current = true;

    if (!modPressedRef.current) {
      hideBadge();
      return;
    }
    const target = e.target as Element | null;
    const host = findHost(target);
    if (host) showBadgeAt(host, e.clientX, e.clientY);
    else hideBadge();
  }, [hideBadge, showBadgeAt]);

  const onMouseOver = useCallback((e: MouseEvent) => {
    lastXRef.current = e.clientX;
    lastYRef.current = e.clientY;
    havePointerPosRef.current = true;
  }, []);

  const onClick = useCallback((e: MouseEvent) => {
    if (!isModDown(e)) return;
    const target = e.target as Element | null;
    const host = findHost(target);
    const id = host?.getAttribute('data-delivery-id');
    if (!id) return;
    e.preventDefault();
    e.stopPropagation();
    window.open(base + encodeURIComponent(id), '_blank', 'noopener');
  }, [base]);

  useEventListener('keydown', onKeyChange, undefined, true);
  useEventListener('keyup', onKeyChange, undefined, true);
  useEventListener('mousemove', onPointerMove, undefined, true);
  useEventListener('mouseover', onMouseOver, undefined, true);
  useEventListener('click', onClick, undefined, true);

  useEffect(() => {
    if (!badgeState.visible || !badgeRef.current) return;

    const badge = badgeRef.current;
    const margin = 8;
    let left = badgeState.x + margin;
    let top = badgeState.y + margin;

    if (left + badge.offsetWidth > window.innerWidth) {
      left = window.innerWidth - badge.offsetWidth - margin;
    }
    if (top + badge.offsetHeight > window.innerHeight) {
      top = window.innerHeight - badge.offsetHeight - margin;
    }

    badge.style.left = `${left}px`;
    badge.style.top = `${top}px`;
  }, [badgeState.visible, badgeState.x, badgeState.y]);

  if (!mounted || typeof window === 'undefined') return null;

  return createPortal(
    <div
      ref={badgeRef}
      style={{
        position: 'fixed',
        zIndex: 2147483647,
        pointerEvents: 'none',
        fontSize: '11px',
        whiteSpace: 'nowrap',
        color: 'white',
        background: 'rgba(0,0,0,0.75)',
        padding: '4px 6px',
        display: badgeState.visible ? 'block' : 'none',
        maxWidth: '60vw',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {badgeState.text}
    </div>,
    document.body
  );
}
